import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { AdminNavEntryPointV3, NavEntryPointV3 } from '@retailer-platform/dashboard/routing'
import { storefrontRouter } from './routing/storefrontRouter'
import {
  storeConfigurationDraftViewAccessControl,
  slotManagerViewAccessControl,
  storefrontHomepageDashboardV2,
  contentPagesSingleWarehouseNavAccessControl,
  contentPagesMultiWarehouseAccessControl,
} from './access-control/storefrontAccess.configuration'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import { autosuggestViewAccessControl } from './access-control/autosuggestAccess.configuration'
import {
  departmentNavigationAccessControl,
  navigationViewAccessControl,
} from './access-control/navigationAccess.configuration'
import { navigationAdminViewAccessControl } from './access-control/storefrontAdminAccess.configuration'
import { cartAssistantSessionDrilldownAccessControl } from './access-control/cartAssistantSessionDrilldownAccess.configuration'
import { storefrontAdminRouter } from './routing/storefrontAdminRouter'
import { storefrontAdminCartAssistantRouter } from './routing/storefrontAdminCartAssistantRouter'
import { storefrontPartnerScopedRouter } from './routing/storefrontPartnerScopedRouter'
import { creativeManagerV2AccessControl } from './access-control/creativeManagerAccess.configuration'
import { Permission } from './access-control/permissions'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'storefront',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'storefront-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    admin: [storefrontAdminRouter, storefrontAdminCartAssistantRouter],
    scoped: [storefrontRouter],
    partnerScoped: [storefrontPartnerScopedRouter],
  },
  navigationEntries: [
    {
      attachToV3: NavEntryPointV3.Storefront,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Configure brand appearance including colors, logos, and custom images for storefront',
          labelId: 'storefrontDomain.brand-settings.page-title',
          labelIdV3: 'storefrontDomain.brand-settings.page-title.v3',
          route: 'storefront-brand-settings-storefront',
          accessControl: storeConfigurationDraftViewAccessControl,
          positionNavV3: 7,
        },
        {
          type: 'item',
          llmDescription:
            'Configure search autosuggest suggestions and quick navigation shortcuts for storefront search bar',
          labelId: 'storefrontDomain.autosuggest-links-list.page-title',
          route: 'storefront-autosuggest-links-list-storefront',
          subRoutes: ['storefront-autosuggest-links-upsert', 'storefront-autosuggest-links-view'],
          accessControl: autosuggestViewAccessControl,
          positionNavV3: 8,
          inToolPermissions: [
            {
              slug: Permission.AutosuggestModify,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.AutosuggestModify,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Configure which product categories and collections appear in your storefront navigation menus',
          labelId: 'storefrontDomain.navigation.collections.title',
          route: 'storefront-navigation-collections-storefront',
          accessControl: departmentNavigationAccessControl,
          positionNavV3: 9,
          inToolPermissions: [
            {
              slug: Permission.DepartmentsModify,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.DepartmentsModify,
            },
          ],
        },
      ],
    },
    {
      attachToV3: NavEntryPointV3.Marketplace,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Configure which product categories and collections appear in your storefront navigation menus',
          labelId: 'storefrontDomain.navigation.collections.title',
          route: 'storefront-marketplace-navigation-collections',
          accessControl: departmentNavigationAccessControl,
          positionNavV3: 5,
          inToolPermissions: [
            {
              slug: Permission.DepartmentsModify,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.DepartmentsModify,
            },
          ],
        },
      ],
    },
    {
      // Each v3 L1's nav items are explicit; SFP attachments below are the
      // only way for SFP-only and SF+SFP retailers to see Storefront tools
      // under the SFP L1.
      attachToV3: NavEntryPointV3.StorefrontPro,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Configure promotional slot placements for featured products and hero banners on storefront',
          labelId: 'storefrontDomain.slotManager.pageTitle',
          route: 'storefront-slots-manager',
          accessControl: slotManagerViewAccessControl,
          positionNavV3: 5,
          inToolPermissions: [
            {
              slug: Permission.StorefrontRetailerFundedMarketingModify,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.StorefrontRetailerFundedMarketingModify,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage promotional creative assets and visual content for campaigns',
          labelId: 'storefrontDomain.creativeManagerV2.nav',
          route: 'storefront-creative-manager-v2',
          accessControl: creativeManagerV2AccessControl,
          positionNavV3: 6,
          dividerBefore: true,
          inToolPermissions: [
            {
              slug: Permission.CreativeManagerModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CreativeManagerModify,
            },
          ],
        },
        {
          type: 'item',
          llmDescription: 'Manage homepage layout and featured content',
          labelId: 'storefrontDomain.homepage.dashboardV2.pageTitle',
          labelIdV3: 'storefrontDomain.homepage.dashboardV2.pageTitle.v3',
          route: 'storefront-homepage-dashboard-v2',
          subRoutes: ['storefront-homepage-edit-v2'],
          accessControl: storefrontHomepageDashboardV2,
          positionNavV3: 7,
        },
        {
          type: 'item',
          llmDescription:
            'Configure brand appearance including colors, logos, and custom images for storefront',
          labelId: 'storefrontDomain.brand-settings.page-title',
          labelIdV3: 'storefrontDomain.brand-settings.page-title.v3',
          route: 'storefront-brand-settings-storefront-pro',
          accessControl: storeConfigurationDraftViewAccessControl,
          positionNavV3: 12,
          dividerBefore: true,
          inToolPermissions: [
            {
              slug: Permission.StoreConfigurationDraftModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StoreConfigurationDraftModify,
            },
            {
              slug: Permission.StoreConfigsModify,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.StoreConfigsModify,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Configure which product categories and collections appear in your storefront navigation menus',
          labelId: 'storefrontDomain.navigation.collections.title',
          route: 'storefront-navigation-collections-storefront-pro',
          accessControl: departmentNavigationAccessControl,
          positionNavV3: 15,
          inToolPermissions: [
            {
              slug: Permission.DepartmentsModify,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.DepartmentsModify,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage custom content pages (legal policies, events, about us) on your storefront',
          labelId: 'storefrontDomain.contentPages.dashboard.pageTitle',
          route: 'storefront-content-pages-dashboard-storefront-pro',
          subRoutes: [
            'storefront-content-pages-create',
            'storefront-content-pages-deleted-storefront-pro',
          ],
          // Complement of contentPagesMultiWarehouseAccessControl — mutually exclusive with the
          // site-scoped entry below, so exactly one content-pages entry shows under SFP.
          accessControl: contentPagesSingleWarehouseNavAccessControl,
          positionNavV3: 9,
          inToolPermissions: [
            {
              slug: Permission.StorefrontPageBuilderModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StorefrontPageBuilderModify,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage custom content pages (legal policies, events, about us) on your storefront',
          labelId: 'storefrontDomain.contentPages.dashboard.pageTitle',
          route: 'storefront-content-pages-dashboard-partner-scoped',
          subRoutes: ['storefront-content-pages-create-partner-scoped'],
          accessControl: contentPagesMultiWarehouseAccessControl,
          positionNavV3: 9,
          inToolPermissions: [
            {
              slug: Permission.StorefrontPageBuilderModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StorefrontPageBuilderModify,
            },
          ],
        },
        {
          type: 'item',
          llmDescription: 'Manage legacy navigation header configuration',
          labelId: 'storefrontDomain.navigation.navigation.legacy.title',
          route: 'storefront-navigation-header-storefront-pro',
          accessControl: navigationViewAccessControl,
          positionNavV3: 14,
          inToolPermissions: [
            {
              slug: Permission.CollectionsView,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.CollectionsView,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Configure search autosuggest suggestions and quick navigation shortcuts for storefront search bar',
          labelId: 'storefrontDomain.autosuggest-links-list.page-title',
          route: 'storefront-autosuggest-links-list-storefront-pro',
          subRoutes: ['storefront-autosuggest-links-upsert', 'storefront-autosuggest-links-view'],
          accessControl: autosuggestViewAccessControl,
          positionNavV3: 17,
          inToolPermissions: [
            {
              slug: Permission.AutosuggestModify,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.AutosuggestModify,
            },
          ],
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      attachToV3: AdminNavEntryPointV3.Marketplace,
      subNavItems: [
        {
          route: 'storefront-admin-navigation-footer',
          labelId: 'storefrontDomain.navigation.footer.mp.nav.title',
          accessControl: navigationAdminViewAccessControl,
          positionNavV3: 8,
          dividerBefore: true,
        },
      ],
    },
    {
      attachToV3: AdminNavEntryPointV3.Marketplace,
      subNavItems: [
        {
          route: 'storefront-admin-cart-assistant-session-drilldown',
          labelId: 'storefrontDomain.cartAssistantSessionDrilldown.nav.title',
          accessControl: cartAssistantSessionDrilldownAccessControl,
          positionNavV3: 9,
        },
      ],
    },
  ],
}

export default definition
